// Free training's log (#157): what you did, logged as you go.
//
// Typing is the main input. One box filters your own exercise list (built from
// your history) and reads any numbers you typed — "bench 80x8", "row 2k",
// "tabata 8 rounds" — so a whole set can be logged in one go. Every entry is
// stamped with the session clock when it's logged, so the log is also the
// session's timeline: the Coach's dial is drawn from it, and there's no Lap.
//
// At Finish the log becomes a real plan tree, the same shape a logged strength
// session has (utils/loggedSession), so history, the AI's training history and
// the recorded weights all read it with no special case.
//
// Pure and React-free, so it can be unit-tested.

import { fromDisplayWeight, showWeight, shownWeightUnit } from "./units";
import { EXERCISES, normaliseLabelTokens, resolveExercise } from "../config/exercises";
import { genStableId } from "./planEditing";
import { ACTIVITY_TYPES } from "./loggedSession";
import { isCalorieUnit, isRecoveryType, isTimedUnit } from "./planNodeUtils";

// ─── Kinds ──────────────────────────────────────────────────────────────────
// lift    weights: sets of reps at a load
// body    bodyweight: sets of reps, no load
// cardio  a piece of distance or time (run, row, bike…)
// block   a timed format of rounds of one move (Tabata, EMOM, AMRAP)

export const KINDS = ["lift", "body", "cardio", "block"];

/** Formats you log as rounds of one move. `work`/`rest` are seconds per round. */
export const BLOCKS = [
  { key: "tabata", name: "Tabata", rounds: 8, work: 20, rest: 10, hint: "20 s on, 10 s off" },
  { key: "emom", name: "EMOM", rounds: 10, work: 60, rest: 0, hint: "Every minute" },
  { key: "amrap", name: "AMRAP", rounds: 5, work: null, rest: 0, hint: "As many rounds as you can" },
];
const BLOCK_BY_KEY = new Map(BLOCKS.map((b) => [b.key, b]));
const blockOf = (name) => {
  const m = String(name || "").match(/\b(tabata|emom|amrap)\b/i);
  return m ? BLOCK_BY_KEY.get(m[1].toLowerCase()) : null;
};

// Moves offered for a block before you've done any.
export const DEFAULT_BLOCK_MOVES = ["Burpee", "Assault Bike", "Kettlebell Swing", "Rower", "Push-up", "Air Squat"];

// Short names people type that the exercise library doesn't resolve on their
// own ("row" alone is ambiguous in a plan label, but not in this box).
const SHORTHANDS = { row: "rower", bike: "spin_bike", ski: "ski_erg", erg: "rower" };
const UNIT_WORDS = new Set(["k", "km", "m", "metre", "meter", "min", "minute", "sec", "second", "kg", "rep", "round", "x"]);
const BY_KEY = new Map(EXERCISES.map((e) => [e.key, e]));

const resolveTyped = (label) => {
  const found = resolveExercise(label);
  if (found) return found;
  // "Row", "Row 2 km": the name with its numbers dropped.
  const words = normaliseLabelTokens(label).filter((t) => !UNIT_WORDS.has(t));
  const short = SHORTHANDS[words.join(" ")];
  return short ? BY_KEY.get(short) || null : null;
};

const kindOfLibraryEntry = (entry) => {
  if (!entry) return null;
  if (entry.load === "erg" || entry.pattern === "locomotion") return "cardio";
  if (entry.load === "bodyweight" || entry.load === "none") return "body";
  return "lift";
};

const tidyName = (raw) => {
  const text = String(raw || "").replace(/\s+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

/**
 * What a typed or logged name is: its display name, the id it's kept under,
 * and its kind (null when the name isn't one the app knows).
 * "bench" and "Bench Press" are one exercise.
 */
export const identifyExercise = (raw) => {
  const block = blockOf(raw);
  if (block && String(raw).trim().split(/\s+/).length === 1) {
    return { id: `block:${block.key}`, name: block.name, kind: "block" };
  }
  const entry = resolveTyped(raw);
  if (entry) return { id: entry.key, name: entry.name, kind: kindOfLibraryEntry(entry) };
  const name = tidyName(raw);
  return { id: name.toLowerCase(), name, kind: null };
};

export const exerciseId = (name) => identifyExercise(name).id;

// A name the library knows word for word (its name, an alias, a shorthand
// like "row"), rather than a description of your own that only contains one
// ("paused bench", "DB bench").
const isPlainName = (name) => {
  const words = normaliseLabelTokens(name).join(" ");
  if (!words || SHORTHANDS[words]) return !!words;
  const entry = resolveTyped(name);
  return !!entry && [entry.name, ...entry.aliases].some((a) => normaliseLabelTokens(a).join(" ") === words);
};

/**
 * One exercise under two names: the same name, or two names the library
 * knows for it ("bench", "Bench Press"). Your own descriptions stay apart.
 */
export const sameExercise = (a, b) => {
  if (exerciseId(a) !== exerciseId(b)) return false;
  return String(a).toLowerCase() === String(b).toLowerCase() || (isPlainName(a) && isPlainName(b));
};

/**
 * Done for time, not reps: a library hold (plank, wall sit, dead hang,
 * carry), one flagged `hold` when added, or one last logged in seconds.
 */
export const isHold = (ex) => {
  if (!ex || (ex.kind !== "lift" && ex.kind !== "body")) return false;
  if (ex.hold) return true;
  if (ex.last && ex.last.s != null && ex.last.reps == null) return true;
  return !!(BY_KEY.get(ex.id) || resolveTyped(ex.name))?.hold;
};

/**
 * A block typed with its move: "tabata burpees" → { block: "Tabata",
 * of: "Burpee" }. Null when the text doesn't start with a block's name or
 * names nothing after it.
 */
export const splitBlockText = (text) => {
  const m = String(text || "").trim().match(/^(tabata|emom|amrap)\s+(?:of\s+)?(.+)$/i);
  if (!m) return null;
  // The move as typed: "tabata burpees" is of "Burpees".
  const of = tidyName(m[2]);
  return of ? { block: BLOCK_BY_KEY.get(m[1].toLowerCase()).name, of } : null;
};

// ─── Numbers ────────────────────────────────────────────────────────────────

/** Seconds as m:ss, or h:mm:ss past the hour. */
export const formatClock = (seconds) => {
  const total = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

/** Metres as "800 m" or "2 km" / "2.5 km". */
export const formatDistance = (metres) => {
  const m = Math.max(0, Math.round(metres || 0));
  if (m < 1000) return `${m} m`;
  const km = m / 1000;
  return `${Number.isInteger(km) ? km : Number(km.toFixed(km < 10 ? 2 : 1))} km`;
};

export const formatLoad = (kg) => (kg > 0 ? showWeight(kg) : "BW");

/** A typed load in kg: as typed with "kg" or "lb", else in the shown unit (#191). */
export const typedLoadKg = (p) =>
  p?.kg == null ? null : p.kgTyped ? p.kg : fromDisplayWeight(p.kg, shownWeightUnit());

// A cardio piece is done by distance, time or calories:
//   { metric: "distance", m, s }  (s: its time, when you gave one)
//   { metric: "time", s }
//   { metric: "cal", cal, s }     (Assault Bike, ski erg… "20 cal")

/** A set's numbers, without its time stamp: { kg, reps } or a hold's { kg, s }. */
export const setOf = (set) =>
  set.s != null
    ? { kg: set.kg || 0, s: set.s }
    : set.cal != null
      ? { kg: set.kg || 0, cal: set.cal }
      : { kg: set.kg || 0, reps: set.reps };

/** "80 kg × 8", "BW × 10", or a hold's "0:45" / "24 kg · 0:45". */
export const formatSet = (set) =>
  set.s != null
    ? `${set.kg > 0 ? `${formatLoad(set.kg)} · ` : ""}${formatClock(set.s)}`
    : set.cal != null
      ? `${set.cal} cal`
      : `${formatLoad(set.kg)} × ${set.reps}`;

/** A piece's figure: "2 km", "10:00", "20 cal". */
export const formatPiece = (piece) => {
  if (!piece) return "";
  if (piece.metric === "distance") return formatDistance(piece.m);
  if (piece.metric === "cal") return `${Math.round(piece.cal || 0)} cal`;
  return formatClock(piece.s);
};

/** The piece typed with a name ("row 2k", "bike 20 cal", "run 30min"), or null. */
export const pieceFromParsed = (p = {}) => {
  if (p.m) return { metric: "distance", m: p.m, s: p.s || null, cal: null };
  if (p.cal) return { metric: "cal", m: null, s: p.s || null, cal: p.cal };
  if (p.s) return { metric: "time", m: null, s: p.s, cal: null };
  return null;
};

/** Just a piece's numbers, in the exercise list's `last` shape. */
export const pieceOf = (x) => ({ metric: x.metric, m: x.m ?? null, s: x.s ?? null, cal: x.cal ?? null });

const DEFAULT_LAST = {
  lift: { kg: 20, reps: 10 },
  body: { kg: 0, reps: 10 },
  cardio: { metric: "distance", m: 1000, s: null },
  block: { rounds: 8, of: "Burpee" },
};

export const defaultLast = (kind, name) => {
  if (kind === "block") {
    const b = blockOf(name);
    return { rounds: b?.rounds ?? 8, of: "Burpee" };
  }
  const hold = (kind === "lift" || kind === "body") && resolveTyped(name)?.hold;
  if (hold) return { kg: kind === "lift" ? 20 : 0, s: resolveTyped(name).pattern === "carry" ? 45 : 30 };
  if (kind === "cardio") {
    // The air bike by calories, machines you'd usually do for time by time,
    // and runs, rows and rides for distance.
    const entry = resolveTyped(name);
    if (entry?.key === "assault_bike") return { metric: "cal", m: null, s: null, cal: 20 };
    const byTime = entry && ["stair_climber", "elliptical"].includes(entry.key);
    return byTime ? { metric: "time", m: null, s: 600 } : { ...DEFAULT_LAST.cardio };
  }
  return { ...(DEFAULT_LAST[kind] || DEFAULT_LAST.lift) };
};

// ─── Reading typed text ─────────────────────────────────────────────────────

/**
 * Pull the numbers out of what was typed, leaving the name.
 *
 *   "bench 80x8"       → { text: "bench", kg: 80, reps: 8 }
 *   "bench 80kg 3x8"   → { text: "bench", kg: 80, sets: 3, reps: 8 }
 *   "bench 3x8 80"     → { text: "bench", sets: 3, reps: 8, kg: 80 }
 *   "bench 3 sets"     → { text: "bench", sets: 3 }
 *   "pull-up x10"      → { text: "pull-up", reps: 10 }
 *   "row 2k"           → { text: "row", m: 2000 }
 *   "row 2k 7:45"      → { text: "row", m: 2000, s: 465 }
 *   "bike 10min"       → { text: "bike", s: 600 }
 *   "bike 20 cal"      → { text: "bike", cal: 20 }
 *   "plank 45s"        → { text: "plank", s: 45 }
 *   "plank 3x45s"      → { text: "plank", sets: 3, s: 45 }
 *   "tabata 8 rounds"  → { text: "tabata", rounds: 8 }
 *
 * `kgTyped` says the load carried a "kg": "3x10" on a bodyweight move is sets
 * by reps, not 3 kg.
 */
// ─── How people actually say it (#159, layer 1: no AI) ───────────────────────
// Voice typing writes numbers as words and people say "for" and "sets of".
// These rewrite it into the shorthand below, before anything else reads it:
//   "bench eighty for eight"   → "bench 80x8"
//   "squat 3 sets of 5 at 100" → "squat 3x5 100"
//   "bench 8 @ 60"             → "bench 60x8"
//   "row two k"                → "row 2 k"

const UNITS = {
  zero: 0, oh: 0, one: 1, a: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19,
};
const TENS = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
const NUMBER_WORD = new RegExp(
  `\\b(?:${[...Object.keys(UNITS), ...Object.keys(TENS), "hundred", "and", "point"].join("|")})\\b`,
  "i",
);

/** "one hundred and twenty two point five" → "122.5"; null when it isn't a number. */
const wordsToNumber = (words) => {
  let total = 0;
  let current = 0;
  let decimals = null;
  let seen = false;
  for (const w of words) {
    if (w === "and") continue;
    if (w === "point") {
      decimals = "";
      continue;
    }
    if (decimals !== null) {
      if (!(w in UNITS) || w === "a") return null;
      decimals += String(UNITS[w]);
      continue;
    }
    if (w === "hundred") {
      current = (current || 1) * 100;
      seen = true;
    } else if (w in TENS) {
      current += TENS[w];
      seen = true;
    } else if (w in UNITS) {
      current += UNITS[w];
      seen = true;
    } else return null;
  }
  if (!seen) return null;
  total += current;
  return decimals ? Number(`${total}.${decimals}`) : total;
};

/** Number words to digits, leaving any other "a" or "and" alone. */
const numbersFromWords = (text) => {
  const tokens = text.split(/(\s+)/);
  const out = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    const lower = tok.toLowerCase().replace(/-/g, " ");
    if (!NUMBER_WORD.test(lower) || /^\s+$/.test(tok)) {
      out.push(tok);
      continue;
    }
    // Gather the run of number words (hyphenated "twenty-five" counts).
    const run = [];
    let j = i;
    for (; j < tokens.length; j += 2) {
      const parts = tokens[j].toLowerCase().split("-");
      if (!parts.every((p) => p in UNITS || p in TENS || ["hundred", "and", "point"].includes(p))) break;
      run.push(...parts);
    }
    // A lone "a" or "and" is a word, not a number.
    while (run.length && ["a", "and"].includes(run[run.length - 1])) {
      run.pop();
      j -= 2;
    }
    const value = run.length && !(run.length === 1 && ["a", "and", "oh", "point"].includes(run[0])) ? wordsToNumber(run) : null;
    if (value === null) {
      out.push(tok);
      continue;
    }
    out.push(String(value));
    // Carry on from the whitespace after the run.
    i = j - 2;
  }
  return out.join("");
};

/** What was said, in the log's shorthand. Pure; safe to run on shorthand too. */
export const normaliseSpoken = (raw) => {
  let t = numbersFromWords(String(raw || ""));
  t = t.replace(/\b(kilos?|kilograms?|kgs)\b/gi, "kg");
  // "100 lb" / "100 pounds" → its kg (#191), so the rest reads one unit.
  t = t.replace(/\b(\d+(?:\.\d+)?)\s*(?:lbs?|pounds?)\b/gi, (_, n) => `${fromDisplayWeight(n, "lb")}kg`);
  // "3 sets of 5 at 100" / "3 sets of 5" → "3x5 100" / "3x5"
  t = t.replace(/\b(\d{1,2})\s*sets?\s+of\s+(\d+)(?:\s*reps?)?(?:\s+(?:at|@|with)\s+(\d+(?:\.\d+)?)\s*(kg)?)?/gi, (_, sets, reps, kg, unit) =>
    kg ? `${sets}x${reps} ${kg}${unit || ""}` : `${sets}x${reps}`,
  );
  // "80 for 8", "80kg for 8 reps" → "80x8"
  t = t.replace(/\b(\d+(?:\.\d+)?)\s*(kg)?\s+for\s+(\d+)(?:\s*reps?)?\b/gi, (_, kg, unit, reps) => `${kg}${unit || ""}x${reps}`);
  // "8 @ 60", "8 reps at 60kg" → "60x8"
  t = t.replace(/\b(\d+)\s*(?:reps?\s*)?(?:@|\bat\b)\s*(\d+(?:\.\d+)?)\s*(kg)?\b/gi, (_, reps, kg, unit) => `${kg}${unit || ""}x${reps}`);
  return t;
};

/**
 * Several entries in one go, "bench 80x8, row 2k then plank 45s": the parts,
 * or just the one when it isn't a list.
 */
export const splitEntries = (raw) =>
  String(raw || "")
    .split(/\s*(?:,|;|\band then\b|\bthen\b)\s*/i)
    .map((x) => x.trim())
    .filter(Boolean);

const parseTyped = (raw) => {
  // `typed` keeps the original case: numbers are blanked out of both alike.
  let typed = ` ${normaliseSpoken(raw).replace(/×/g, "x")} `;
  let s = typed.toLowerCase();
  const aligned = s.length === typed.length;
  const out = {};
  let m;
  const take = (match) => {
    const i = s.indexOf(match[0]);
    const blank = " ".repeat(match[0].length);
    s = s.slice(0, i) + blank + s.slice(i + match[0].length);
    if (aligned) typed = typed.slice(0, i) + blank + typed.slice(i + match[0].length);
  };
  // Sets by reps with the load after: "3x8 80", "3x8 @ 80kg" (#164).
  const after = s.match(/\s(\d{1,2})\s*x\s*(\d+)(?:\s*@\s*|\s+)(\d+(?:\.\d+)?)\s*(kg)?(?=\s)/);
  // Sets of a hold: "3x45s", "3 x 1:00".
  const held = s.match(/\s(\d{1,2})\s*x\s*(?:(\d+)\s*(?:s|secs?|seconds)|(\d+):(\d{2}))(?=\s)/);
  if (held) {
    out.sets = +held[1];
    out.s = held[2] ? +held[2] : +held[3] * 60 + +held[4];
    take(held);
  } else if (after && +after[1] <= 10) {
    out.sets = +after[1];
    out.reps = +after[2];
    out.kg = +after[3];
    out.kgTyped = true;
    take(after);
  } else if ((m = s.match(/\s(\d+(?:\.\d+)?)\s*(kg)?\s+(\d+)\s*x\s*(\d+)(?:\s*reps?)?(?=\s)/))) {
    out.kg = +m[1];
    out.kgTyped = !!m[2];
    out.sets = +m[3];
    out.reps = +m[4];
    take(m);
  } else if ((m = s.match(/\s(\d+(?:\.\d+)?)\s*(kg)?\s*x\s*(\d+)(?:\s*reps?)?(?=\s)/))) {
    out.kg = +m[1];
    out.kgTyped = !!m[2];
    out.reps = +m[3];
    take(m);
  } else if ((m = s.match(/\s(?:x\s*(\d+)|(\d+)\s*reps?)(?=\s)/))) {
    out.reps = +(m[1] || m[2]);
    take(m);
  }
  if ((m = s.match(/\s(\d+)\s*sets?(?=\s)/))) {
    out.sets = +m[1];
    take(m);
  }
  if ((m = s.match(/\s(\d+(?:\.\d+)?)\s*(?:k|km)(?=\s)/))) {
    out.m = Math.round(+m[1] * 1000);
    take(m);
  } else if ((m = s.match(/\s(\d+)\s*m(?=\s)/))) {
    out.m = +m[1];
    take(m);
  }
  if ((m = s.match(/\s(\d+)\s*(?:min|mins|minutes?)(?=\s)/))) {
    out.s = +m[1] * 60;
    take(m);
  } else if ((m = s.match(/\s(\d+):(\d{2})(?=\s)/))) {
    out.s = +m[1] * 60 + +m[2];
    take(m);
  } else if ((m = s.match(/\s(\d+)\s*(?:s|secs?|seconds)(?=\s)/))) {
    out.s = +m[1];
    take(m);
  }
  if ((m = s.match(/\s(\d+)\s*(?:cals?|calories|kcal)(?=\s)/))) {
    out.cal = +m[1];
    take(m);
  }
  if ((m = s.match(/\s(\d+)\s*(?:rounds?|rds?)(?=\s)/))) {
    out.rounds = +m[1];
    take(m);
  }
  if ((m = s.match(/\s(\d+(?:\.\d+)?)\s*kg(?=\s)/)) && out.kg == null) {
    out.kg = +m[1];
    out.kgTyped = true;
    take(m);
  }
  out.text = s.replace(/\s+/g, " ").trim();
  return { out, name: aligned ? typed.replace(/\s+/g, " ").trim() : out.text };
};

export const parseEntryText = (raw) => parseTyped(raw).out;

/**
 * The name in what was typed, as typed, less its numbers: "DB bench 30x10"
 * → "DB bench". Only the first letter is raised. What you type is what's
 * logged; the library and your list only say what it is.
 */
export const typedName = (raw) => tidyName(parseTyped(raw).name);

/** True when the typed numbers are enough to log without asking for more. */
export const hasNumbers = (p) => p.reps != null || !!p.m || !!p.s || !!p.cal || !!p.rounds;

// ─── Your exercise list ─────────────────────────────────────────────────────

const lastOfScheme = (node, dim) => {
  const arr = node?.setScheme?.[dim];
  return Array.isArray(arr) && arr.length ? arr[arr.length - 1] : node?.[dim];
};

const distanceInLabel = (label) => {
  const m = String(label || "").toLowerCase().match(/(\d+(?:\.\d+)?)\s*(km|k|m)\b/);
  if (!m) return null;
  return m[2] === "m" ? Math.round(+m[1]) : Math.round(+m[1] * 1000);
};

// The numbers a plan step records, in the log's terms.
const lastFromLeaf = (leaf, kind) => {
  if (kind === "cardio") {
    if (isCalorieUnit(leaf.unit) && Number(leaf.value) > 0) {
      return { metric: "cal", m: null, s: null, cal: Number(leaf.value) };
    }
    const metres = distanceInLabel(leaf.label);
    const secs = isTimedUnit(leaf.unit)
      ? Number(leaf.value) * (String(leaf.unit).toLowerCase() === "m" ? 60 : 1)
      : null;
    if (metres) return { metric: "distance", m: metres, s: secs || null };
    if (secs > 0) return { metric: "time", m: null, s: secs };
    return null;
  }
  if (isTimedUnit(leaf.unit)) {
    // A hold's sets are timed; another move's timed step isn't a set of it.
    if (!resolveTyped(leaf.label)?.hold) return null;
    const secs = Number(lastOfScheme(leaf, "value")) * (String(leaf.unit).toLowerCase() === "m" ? 60 : 1);
    const kg = Number(lastOfScheme(leaf, "weight"));
    return secs > 0 ? { kg: kg > 0 ? kg : 0, s: secs } : null;
  }
  if (String(leaf.unit).toLowerCase() !== "reps") return null;
  const reps = Number(lastOfScheme(leaf, "value"));
  if (!(reps > 0)) return null;
  const kg = Number(lastOfScheme(leaf, "weight"));
  return { kg: kg > 0 ? kg : 0, reps };
};

const workLeaves = (node, out = []) => {
  if (!node || typeof node !== "object") return out;
  if (Array.isArray(node.children) && node.children.length) {
    // A block you logged is one exercise: its rounds, of its move.
    const block = blockOf(node.label);
    if (block && node.unit === "rounds") {
      const move = node.children.find((c) => !isRecoveryType(c.type));
      out.push({ block, rounds: Number(node.value) || block.rounds, of: move?.label || "Burpee" });
      return out;
    }
    node.children.forEach((c) => workLeaves(c, out));
    return out;
  }
  if (!isRecoveryType(node.type) && node.label) out.push(node);
  return out;
};

// Stopwatch steps aren't exercises.
const NOT_EXERCISES = new Set(["free training", "stopwatch", "count up", "work", "exercise"]);

const toTime = (session) => {
  const t = new Date(session?.started_at || session?.ended_at || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

/**
 * Your exercise list, from saved sessions (plan steps, logged and
 * free-training entries) and benchmarks. Each entry:
 *   { id, name, kind, n, last }
 * `n` is how many sessions it's in; `last` the numbers from the latest one.
 */
export const buildExerciseList = (sessions = [], capabilities = []) => {
  const byId = new Map();
  const touch = (raw, kind, last, sessionKey) => {
    const ident = identifyExercise(raw);
    if (!ident.name || NOT_EXERCISES.has(ident.name.toLowerCase())) return;
    const k = ident.kind || kind;
    if (!k) return;
    let entry = byId.get(ident.id);
    if (!entry) {
      entry = { id: ident.id, name: ident.name, kind: k, n: 0, last: null, seen: null };
      byId.set(ident.id, entry);
    }
    if (sessionKey != null && entry.seen !== sessionKey) {
      entry.n += 1;
      entry.seen = sessionKey;
    }
    if (last) entry.last = last;
  };

  const ordered = (Array.isArray(sessions) ? sessions : [])
    .filter((s) => s && typeof s === "object")
    .map((s, i) => ({ s, i, t: toTime(s) }))
    .sort((a, b) => a.t - b.t || a.i - b.i);

  for (const { s, i } of ordered) {
    const key = s.id ?? `i${i}`;
    const plan = s.plan_data;
    if (plan && typeof plan === "object" && !plan.freeTraining) {
      for (const leaf of workLeaves(plan)) {
        if (leaf.block) {
          touch(leaf.block.name, "block", { rounds: leaf.rounds, of: tidyName(leaf.of) }, key);
          continue;
        }
        const ident = identifyExercise(leaf.label);
        const kind =
          ident.kind ||
          (isTimedUnit(leaf.unit) || isCalorieUnit(leaf.unit) ? "cardio" : Number(leaf.weight) > 0 ? "lift" : "body");
        touch(leaf.label, kind, lastFromLeaf(leaf, kind), key);
      }
    } else if (s.activity && typeof s.activity === "object") {
      // A logged run, ride or row: the activity is the exercise.
      const type = ACTIVITY_TYPES.find((a) => a.label === s.workout_type && a.tracksDistance);
      if (!type) continue;
      const metres = Number(s.activity.distance_m) || null;
      const secs = Number(s.duration_minutes) > 0 ? Number(s.duration_minutes) * 60 : null;
      touch(
        type.value === "walk" ? "Walk" : type.label,
        "cardio",
        metres ? { metric: "distance", m: metres, s: secs } : secs ? { metric: "time", m: null, s: secs } : null,
        key,
      );
    }
  }

  // Benchmarks name what you do even when no session has yet.
  for (const cap of Array.isArray(capabilities) ? capabilities : []) {
    if (!cap?.label) continue;
    const ident = identifyExercise(cap.label);
    if (!ident.kind || byId.has(ident.id)) continue;
    touch(cap.label, ident.kind, null, null);
  }

  return [...byId.values()].map(({ seen, ...e }) => ({
    ...e,
    last: e.last || defaultLast(e.kind, e.name),
  }));
};

// ─── This session's log ─────────────────────────────────────────────────────
// The log is plan steps: each entry is the node it's saved as, so saving is
// taking the times off, and the plan editor edits an entry as it would any
// step. Each node also carries `at`, the session clock (seconds) when it was
// logged: an array, one per set, on a sets block; a number on a piece or a
// block of rounds.
//
//   sets    Group(unit "sets", value n, at [..]) > Work(label, unit "reps",
//           value, weight, setScheme)        — a hold's Work is unit "s"
//   piece   Work(label "Row 2 km", unit "s" | "cal", value, at)
//           — a distance rides in the label, as in any plan; the time is
//           null until known (filled from the clock when saved)
//   block   Group(label "Tabata: Burpees", unit "rounds", value, at) >
//           Work (+ Rest)
//
// The log's own screens read an entry through `entryView`, the flat shape
// they were written for:
//   { id, name, kind: "lift"|"body", sets: [{ kg, reps | s | cal, at }] }
//   { id, name, kind: "cardio", metric: "distance"|"time"|"cal", m, s, cal, at }
//   { id, name, kind: "block", rounds, of, at }

const isNode = (x) => !!x && typeof x.type === "string";
const VIEW_SOURCE = new WeakMap();
const strip = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out;
};
const secondsOf = (value, unit) => (Number(value) || 0) * (String(unit).toLowerCase() === "m" ? 60 : 1);

/** An entry as the log's screens read it (see above). Takes a node or a view. */
export const entryView = (entry) => {
  if (!isNode(entry)) return entry;
  let view;
  const atList = Array.isArray(entry.at) ? entry.at : entry.at != null ? [entry.at] : [];
  if (Array.isArray(entry.children) && entry.children.length) {
    const block = blockOf(entry.label);
    if (block && entry.unit === "rounds") {
      const move = entry.children.find((c) => !isRecoveryType(c.type));
      const of = String(entry.label).split(":").slice(1).join(":").trim() || tidyName(move?.label) || "Burpee";
      view = { id: entry.id, name: block.name, kind: "block", rounds: Number(entry.value) || block.rounds, of, at: atList[0] ?? 0 };
    } else {
      const work = entry.children.find((c) => !isRecoveryType(c.type)) || entry.children[0];
      const n = Math.max(1, Math.round(Number(entry.value) || 1));
      const unit = String(work.unit || "reps").toLowerCase();
      const sets = Array.from({ length: n }, (_, i) => {
        const v = nth(work.setScheme?.value, i, work.value);
        const kg = Math.max(0, Number(nth(work.setScheme?.weight, i, work.weight)) || 0);
        const at = atList[i] ?? atList[atList.length - 1] ?? 0;
        if (unit === "cal") return { kg, cal: Number(v) || 0, at };
        if (isTimedUnit(unit)) return { kg, s: secondsOf(v, unit), at };
        return { kg, reps: Math.max(1, Math.round(Number(v) || 1)), at };
      });
      const kind = sets.some((x) => x.kg > 0) ? "lift" : identifyExercise(entry.label).kind === "lift" ? "lift" : "body";
      view = { id: entry.id, name: entry.label, kind, sets };
    }
  } else if (String(entry.unit).toLowerCase() === "reps") {
    view = {
      id: entry.id,
      name: entry.label,
      kind: Number(entry.weight) > 0 ? "lift" : "body",
      sets: [{ kg: Number(entry.weight) || 0, reps: Number(entry.value) || 1, at: atList[0] ?? 0 }],
    };
  } else {
    const m = distanceInLabel(entry.label);
    const name = typedName(entry.label) || entry.label;
    const base = { id: entry.id, name, kind: "cardio", at: atList[0] ?? 0 };
    // A piece a paired machine logged (#158): when it started, and from what.
    if (entry.from != null) base.from = entry.from;
    if (entry.source) base.source = entry.source;
    if (String(entry.unit).toLowerCase() === "cal") view = { ...base, metric: "cal", m: null, s: null, cal: Number(entry.value) || 0 };
    else {
      const s = entry.value == null ? null : secondsOf(entry.value, entry.unit);
      view = m ? { ...base, metric: "distance", m, s, cal: null } : { ...base, metric: "time", m: null, s, cal: null };
    }
  }
  view.node = entry;
  VIEW_SOURCE.set(view, entry);
  return view;
};

/** A view as its node, keeping whatever else the node carried (its type, effort, notes). */
const entryNode = (view) => {
  if (isNode(view)) return view;
  // A view straight from a node, unchanged, is that node. A changed copy
  // carries its node along (`node`), so the rest of the step survives.
  const source = VIEW_SOURCE.get(view);
  if (source) return source;
  const orig = view.node || null;
  const id = view.id || genStableId();
  if (view.kind === "block") {
    const built = blockGroup(view, 0);
    return strip({ ...built, ...(orig || {}), id, label: built.label, unit: "rounds", value: built.value, children: orig?.children?.length && entryView(orig).of === view.of ? orig.children : built.children, at: view.at });
  }
  if (view.sets) {
    const held = view.sets.some((x) => x.s != null);
    const cals = !held && view.sets.some((x) => x.cal != null);
    const values = view.sets.map((x) => (held ? x.s : cals ? x.cal : x.reps));
    const kgs = view.sets.map((x) => x.kg || 0);
    const loaded = kgs.some((k) => k > 0);
    const scheme = {};
    if (!same(values)) scheme.value = values;
    if (loaded && !same(kgs)) scheme.weight = kgs;
    const origWork = orig?.children?.find((c) => !isRecoveryType(c.type)) || orig?.children?.[0] || (orig && !orig.children ? orig : null);
    const work = strip({
      ...(origWork && origWork !== orig ? origWork : {}),
      id: origWork && origWork !== orig ? origWork.id : genStableId(),
      type: origWork?.type || "Work",
      label: view.name,
      unit: held ? "s" : cals ? "cal" : "reps",
      value: values[0],
      weight: loaded ? kgs[0] : undefined,
      intensityPct: loaded ? undefined : origWork?.intensityPct,
      setScheme: Object.keys(scheme).length ? scheme : undefined,
      at: undefined,
    });
    return strip({
      ...(orig && orig.children ? orig : {}),
      id,
      type: "Group",
      label: view.name,
      unit: "sets",
      value: view.sets.length,
      children: [work, ...((orig?.children || []).filter((c) => c !== origWork && isRecoveryType(c.type)))],
      at: view.sets.map((x) => x.at ?? 0),
    });
  }
  // A piece.
  const label = view.metric === "distance" && view.m ? `${view.name} ${formatDistance(view.m)}` : view.name;
  return strip({
    ...(orig && !orig.children ? orig : {}),
    id,
    type: orig && !orig.children ? orig.type : "Work",
    label,
    unit: view.metric === "cal" ? "cal" : "s",
    value: view.metric === "cal" ? view.cal : view.s ?? null,
    at: view.at ?? 0,
    from: view.from ?? undefined,
    source: view.source || undefined,
  });
};

const views = (log = []) => (Array.isArray(log) ? log : []).map(entryView);
const nodes = (vs) => vs.map(entryNode);

/** A log as nodes: any entries still in the old flat shape are converted. */
export const normaliseLog = (log) => nodes(views(log));

/** When the entry was last logged to. */
export const entryAt = (node) => {
  const entry = entryView(node);
  return entry?.sets ? entry.sets[entry.sets.length - 1]?.at ?? 0 : entry?.at ?? 0;
};

export const lastLoggedAt = (log = []) => views(log).reduce((a, e) => Math.max(a, entryAt(e)), 0);

/** Sets, pieces and blocks logged. */
export const loggedCount = (log = []) =>
  views(log).reduce((n, e) => n + (e.sets ? e.sets.length : 1), 0);

/** The numbers an entry ended on, as an exercise list's `last`. */
export const lastOfEntry = (node) => {
  const entry = entryView(node);
  if (entry.sets) return setOf(entry.sets[entry.sets.length - 1]);
  if (entry.kind === "cardio") return pieceOf(entry);
  return { rounds: entry.rounds, of: entry.of };
};

/**
 * Add sets. A set of what you're already on, by the same name, joins that
 * entry; anything else starts a new one.
 */
export const addSets = (logNodes, { name, kind, kg = 0, reps, s = null, cal = null, count = 1 }, at) => {
  const log = views(logNodes);
  const tail = log[log.length - 1];
  const sets = Array.from({ length: Math.max(1, count) }, () => ({
    // Bodyweight moves start at none, but can carry a load (a weighted pull-up).
    kg: Math.max(0, kg || 0),
    // A hold's set is its time; sets of calories are calories.
    ...(s > 0 ? { s: Math.round(s) } : cal > 0 ? { cal: Math.round(cal) } : { reps: Math.max(1, Math.round(reps || 1)) }),
    at,
  }));
  // "Paused bench" after "DB bench" is its own entry, though both are bench
  // press to the library.
  if (tail?.sets && sameExercise(tail.name, name)) {
    return nodes([...log.slice(0, -1), { ...tail, sets: [...tail.sets, ...sets] }]);
  }
  return nodes([...log, { id: genStableId(), name, kind, sets }]);
};

export const addPiece = (log, { name, metric, m = null, s = null, cal = null, from, source }, at) =>
  nodes([
    ...views(log),
    strip({ id: genStableId(), name, kind: "cardio", metric, m, s, cal, at, from, source }),
  ]);

export const addBlock = (log, { name, rounds, of }, at) =>
  nodes([...views(log), { id: genStableId(), name, kind: "block", rounds, of, at }]);

/** Same again / Duplicate: one more of this entry, logged now. */
export const repeatEntry = (log, entryId, at) => {
  const entry = views(log).find((e) => e.id === entryId);
  if (!entry) return log;
  if (entry.sets) {
    const last = entry.sets[entry.sets.length - 1];
    return addSets(log, { name: entry.name, kind: entry.kind, ...setOf(last) }, at);
  }
  if (entry.kind === "cardio") return addPiece(log, entry, at);
  return addBlock(log, entry, at);
};

export const updateEntry = (log, entryId, patch) =>
  nodes(views(log).map((e) => (e.id === entryId ? { ...e, ...patch } : e)));

/**
 * Change an entry's node from the plan editor's step dialog: `updates` are
 * the step's fields (label, type, unit, value, weight, setScheme…). On a sets
 * block they land on its step, and the block takes the step's name.
 */
export const editEntryNode = (log, entryId, updates) =>
  (Array.isArray(log) ? log : []).map((node) => {
    if (node.id !== entryId) return node;
    if (Array.isArray(node.children) && node.children.length && node.unit === "sets") {
      const i = node.children.findIndex((c) => !isRecoveryType(c.type));
      const children = node.children.map((c, j) => (j === Math.max(0, i) ? strip({ ...c, ...updates }) : c));
      return strip({ ...node, label: updates.label ? typedName(updates.label) || updates.label : node.label, children });
    }
    return strip({ ...node, ...updates, at: node.at, children: updates.children ?? node.children });
  });

export const deleteEntry = (log, entryId) => (Array.isArray(log) ? log : []).filter((e) => e.id !== entryId);

export const updateSet = (log, entryId, index, patch) =>
  nodes(
    views(log).map((e) =>
      e.id === entryId && e.sets
        ? { ...e, sets: e.sets.map((s, i) => (i === index ? { ...s, ...patch } : s)) }
        : e,
    ),
  );

/** Remove one set; an entry left with none goes too. */
export const deleteSet = (log, entryId, index) =>
  nodes(
    views(log).flatMap((e) => {
      if (e.id !== entryId || !e.sets) return [e];
      const sets = e.sets.filter((_, i) => i !== index);
      return sets.length ? [{ ...e, sets }] : [];
    }),
  );

/** One line for a toast or a summary: "Bench Press · 80 kg × 8". */
export const describeEntry = (node) => {
  const entry = entryView(node);
  if (entry.sets) return `${entry.name} · ${formatSet(entry.sets[entry.sets.length - 1])}`;
  if (entry.kind === "cardio") {
    return `${entry.name} · ${formatPiece(entry)}`;
  }
  return `${entry.name} · ${entry.rounds} × ${entry.of}`;
};

/** The figure at the end of an exercise list row or quick button. */
export const describeLast = (kind, last) => {
  if (!last) return "";
  if (kind === "cardio") return formatPiece(last);
  if (kind === "block") return `${last.rounds} rounds`;
  return formatSet(last);
};

// ─── Suggestions ────────────────────────────────────────────────────────────

/**
 * Your list with this session folded in: anything logged today is on it,
 * flagged `inSession`, with today's numbers as its last.
 */
export const withSession = (list = [], log = []) => {
  const byId = new Map(list.map((e) => [e.id, { ...e }]));
  for (const entry of views(log)) {
    const id = exerciseId(entry.name);
    const existing = byId.get(id);
    byId.set(id, {
      ...(existing || { id, name: entry.name, kind: entry.kind, n: 0 }),
      kind: entry.kind,
      last: lastOfEntry(entry),
      inSession: true,
    });
  }
  return [...byId.values()];
};

const scoreName = (name, aliases, text, words) => {
  const n = name.toLowerCase();
  let score = 0;
  if (n.startsWith(text)) score = 3;
  else if (n.split(/[\s-]/).some((w) => w.startsWith(words[0]))) score = 2;
  else if (n.includes(text)) score = 1;
  else if (aliases.some((a) => a.startsWith(text))) score = 1.5;
  if (score && words.length > 1 && !words.every((w) => n.includes(w) || aliases.some((a) => a.includes(w)))) {
    score = 0;
  }
  return score;
};

const CATALOGUE = [
  ...BLOCKS.map((b) => ({ id: `block:${b.key}`, name: b.name, kind: "block", aliases: [] })),
  ...EXERCISES.map((e) => ({
    id: e.key,
    name: e.name,
    kind: kindOfLibraryEntry(e),
    aliases: e.aliases,
    staple: !!e.staple,
  })),
];

/**
 * Type-ahead: your exercises that match, what's left on the checklist first
 * (flagged `onChecklist`), then this session's, then the most used. When your list runs short, the app's own exercise library fills in,
 * so "deadl" finds Deadlift before you've ever logged one.
 */
export const suggestExercises = (list = [], text = "", limit = 4) => {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return [];
  const words = t.split(/\s+/);
  const resolved = identifyExercise(t);
  const own = list
    .map((e) => {
      let score = scoreName(e.name, [], t, words);
      if (!score && resolved.kind && resolved.id === e.id) score = 2.5;
      return { e, score: score ? score + (e.onChecklist ? 20 : 0) + (e.inSession ? 10 : 0) + 0.5 : 0 };
    })
    .filter((x) => x.score);
  const ids = new Set(list.map((e) => e.id));
  const library = CATALOGUE.filter((c) => !ids.has(c.id))
    .map((c) => {
      let score = scoreName(c.name, c.aliases, t, words);
      if (!score && resolved.kind && resolved.id === c.id) score = 2.5;
      // Between equal matches, the staples: "dead" is Deadlift before Dead Bug.
      if (score && c.staple) score += 0.2;
      return { e: { id: c.id, name: c.name, kind: c.kind, n: 0, last: defaultLast(c.kind, c.name) }, score };
    })
    .filter((x) => x.score);
  return [...own, ...library]
    .sort((a, b) => b.score - a.score || (b.e.n || 0) - (a.e.n || 0) || a.e.name.localeCompare(b.e.name))
    .slice(0, limit)
    .map((x) => x.e);
};

/**
 * The quick buttons: `first` (what's left on the checklist), then this
 * session's exercises (newest first), then your most used. What you're on now
 * is left out: its entry has Same again.
 */
export const quickPicks = (list = [], log = [], count = 3, first = []) => {
  const logged = views(log);
  const tail = logged[logged.length - 1];
  const tailId = tail ? exerciseId(tail.name) : null;
  const byId = new Map(list.map((e) => [e.id, e]));
  const sessionIds = [...new Set(logged.map((e) => exerciseId(e.name)).reverse())];
  const picks = [];
  for (const e of first) {
    if (picks.length >= count) break;
    if (e.id !== tailId && !picks.some((p) => p.id === e.id)) picks.push(e);
  }
  for (const id of [...sessionIds, ...[...list].sort((a, b) => b.n - a.n).map((e) => e.id)]) {
    if (picks.length >= count) break;
    if (id === tailId || picks.some((p) => p.id === id)) continue;
    const e = byId.get(id);
    if (e) picks.push(e);
  }
  return picks;
};

// ─── Checklist (#164) ───────────────────────────────────────────────────────
// What you mean to do, jotted before you start: a list to tick off in any
// order, with no timings. Items have the log entries' shape, without `at`:
//   { id, name, kind: "lift"|"body", sets: [{ kg, reps }] | null }
//   { id, name, kind: "cardio", metric: "distance"|"time"|"cal"|null, m, s, cal }
//   { id, name, kind: "block", rounds, of }
// `sets: null` and `metric: null` mean no numbers yet: last time's are used.

/**
 * What was typed for the checklist. A small first number with no kg is sets
 * here, not a load: "bench 3x8" is three sets of eight.
 */
export const parseChecklistText = (raw) => {
  const p = parseEntryText(raw);
  if (p.kg != null && !p.kgTyped && p.sets == null && Number.isInteger(p.kg) && p.kg <= 10) {
    const { kg, kgTyped, ...rest } = p;
    return { ...rest, sets: kg };
  }
  return p;
};

/** A checklist item for `ex` (an exercise list entry), with any typed numbers. */
export const checklistItem = (ex, p = {}) => {
  const last = ex.last || defaultLast(ex.kind, ex.name);
  const base = { id: genStableId(), name: ex.name, kind: ex.kind };
  if (ex.kind === "cardio") {
    return { ...base, ...(pieceFromParsed(p) || { metric: null, m: null, s: null, cal: null }) };
  }
  if (ex.kind === "block") return { ...base, rounds: p.rounds || last.rounds || 8, of: p.of || last.of || "Burpee" };
  if (p.reps == null && p.sets == null && p.kg == null && p.s == null) return { ...base, sets: null };
  if (isHold(ex)) {
    const kg = Math.max(0, p.kg ?? last.kg ?? 0);
    const secs = Math.max(1, Math.round(p.s ?? last.s ?? 30));
    return { ...base, sets: Array.from({ length: Math.max(1, Math.min(20, p.sets || 1)) }, () => ({ kg, s: secs })) };
  }
  const kg = Math.max(0, p.kg ?? last.kg ?? 0);
  const reps = Math.max(1, Math.round(p.reps ?? last.reps ?? 10));
  return { ...base, sets: Array.from({ length: Math.max(1, Math.min(20, p.sets || 1)) }, () => ({ kg, reps })) };
};

/**
 * "bench 3x8 80", "pull-ups", "row 2k", "tabata burpees" → a checklist item,
 * or null when nothing was named. Names resolve through your list first.
 */
export const checklistFromText = (raw, list = []) => {
  const p = parseChecklistText(raw);
  const block = splitBlockText(typedName(raw));
  if (block) {
    const ex = list.find((e) => e.name === block.block) || { name: block.block, kind: "block" };
    return checklistItem(ex, { ...p, of: block.of });
  }
  if (!p.text) return null;
  // Named as typed; your list or the library only fill in what it is.
  const ident = identifyExercise(p.text);
  const known = list.find((e) => e.id === ident.id);
  if (known) return checklistItem({ ...known, name: typedName(raw) }, p);
  const kind = ident.kind || (p.m || p.s || p.cal ? "cardio" : p.rounds ? "block" : p.kg ? "lift" : p.reps != null || p.sets != null ? "body" : "lift");
  return checklistItem({ id: ident.id, name: typedName(raw), kind }, p);
};

/** An item's planned numbers as one line, or null when it has none yet. */
export const checklistFigure = (item) => {
  if (item.kind === "cardio") {
    return item.metric ? formatPiece(item) : null;
  }
  if (item.kind === "block") return `${item.rounds} × ${item.of}`;
  if (!item.sets?.length) return null;
  const s = item.sets[0];
  const each = s.s != null ? formatClock(s.s) : s.reps;
  return `${item.sets.length} × ${each}${s.kg > 0 ? ` · ${formatLoad(s.kg)}` : ""}`;
};

/** The item with any missing numbers filled from your list (or defaults). */
export const withChecklistNumbers = (item, list = []) => {
  const last = list.find((e) => e.id === exerciseId(item.name))?.last || defaultLast(item.kind, item.name);
  if (item.kind === "cardio") {
    return item.metric ? item : { ...item, ...pieceOf({ ...last, metric: last.metric || "distance" }) };
  }
  if (item.kind === "block" || item.sets?.length) return item;
  const one = isHold({ ...item, id: exerciseId(item.name), last })
    ? { kg: last.kg ?? 0, s: last.s ?? 30 }
    : { kg: last.kg ?? 0, reps: last.reps ?? 10 };
  return { ...item, sets: Array.from({ length: 3 }, () => ({ ...one })) };
};

/**
 * How far through the checklist the log is, item by item: `planned` sets (or
 * 1 for a piece, a block, or sets with no numbers), `done`, and `left`. Only
 * the log counts, so it's the same whatever order things were done in. An
 * exercise on the list twice takes its sets in list order.
 */
export const checklistProgress = (checklist = [], log = []) => {
  // Each log entry's sets, spent on the rows in order. "Paused bench" doesn't
  // tick off "Bench"; "Bench Press" does.
  const pool = views(log).map((e) => ({ name: e.name, left: e.sets ? e.sets.length : 1 }));
  return checklist.map((item) => {
    const planned = item.sets?.length || 1;
    let used = 0;
    for (const p of pool) {
      if (used >= planned) break;
      if (p.left > 0 && sameExercise(p.name, item.name)) {
        const take = Math.min(p.left, planned - used);
        p.left -= take;
        used += take;
      }
    }
    return { item, planned, done: used, left: planned - used };
  });
};

/**
 * The numbers the next log of a checklist row uses: the item's own (the next
 * set's), else your last (today's, once `list` has the session folded in).
 * In the exercise list's `last` shape.
 */
export const checklistNext = ({ item, done = 0 }, list = []) => {
  const last = list.find((e) => e.id === exerciseId(item.name))?.last || defaultLast(item.kind, item.name);
  if (item.kind === "cardio") return item.metric ? pieceOf(item) : last;
  if (item.kind === "block") return { rounds: item.rounds, of: item.of };
  if (item.sets?.length) return setOf(item.sets[Math.min(done, item.sets.length - 1)]);
  return last.s != null ? { kg: last.kg ?? 0, s: last.s } : { kg: last.kg ?? 0, reps: last.reps ?? 10 };
};

/**
 * What's left of a checklist row as log entries (nodes) stamped `at`: for
 * adding at Finish what was done but not logged.
 */
export const checklistLeftEntries = (row, list = [], at = 0) => leftViews(row, list, at).map(entryNode);

const leftViews = (row, list = [], at = 0) => {
  if (!row || row.left <= 0) return [];
  const item = withChecklistNumbers(row.item, list);
  if (item.kind === "cardio") return [{ id: genStableId(), name: item.name, kind: "cardio", ...pieceOf(item), at }];
  if (item.kind === "block") return [{ id: genStableId(), name: item.name, kind: "block", rounds: item.rounds, of: item.of, at }];
  const sets = row.item.sets?.length ? item.sets.slice(row.done) : [checklistNext(row, list)];
  return [{ id: genStableId(), name: item.name, kind: item.kind, sets: sets.map((s) => ({ ...setOf(s), at })) }];
};

// A step's name as the session had it, less any numbers ("Row 2 km" → "Row").
const nameOf = (label) => typedName(label) || identifyExercise(label).name;
const REPEAT_UNITS = new Set(["sets", "rounds"]);
const nth = (arr, i, fallback) => (Array.isArray(arr) && arr.length ? arr[Math.min(i, arr.length - 1)] : fallback);

// The checklist is a plan: { type "Group", label "Checklist", children }
// holding ordinary steps, edited with the plan editor like any session.
export const CHECKLIST_LABEL = "Checklist";

/**
 * The checklist's rows to tick off, each in the log's terms (see entryView)
 * and keyed by its step's id. A sets or rounds block repeats what's in it; a
 * step for time is timed sets, unless it's cardio (then a piece); a block of
 * Tabata/EMOM/AMRAP is one row. Rests are left out.
 */
export const checklistItems = (plan) => {
  const out = [];
  const walk = (node, times, path) => {
    if (!node || typeof node !== "object") return;
    const id = node.id || `c${path}`;
    if (Array.isArray(node.children) && node.children.length) {
      const block = blockOf(node.label);
      if (block && node.unit === "rounds") {
        const move = node.children.find((c) => !isRecoveryType(c.type));
        out.push({ id, name: block.name, kind: "block", rounds: Number(node.value) || block.rounds, of: tidyName(move?.label) || "Burpee" });
        return;
      }
      const n = Math.round(Number(node.value));
      const next = REPEAT_UNITS.has(String(node.unit || "").toLowerCase()) && n > 1 ? times * n : times;
      node.children.forEach((c, i) => walk(c, next, `${path}.${i}`));
      return;
    }
    if (isRecoveryType(node.type) || !node.label) return;
    const name = nameOf(node.label);
    if (!name || NOT_EXERCISES.has(name.toLowerCase())) return;
    const ident = identifyExercise(node.label);
    const unit = String(node.unit || "").toLowerCase();
    const count = Math.max(1, Math.min(20, times));
    const piece = isCalorieUnit(unit) || ((isTimedUnit(unit) || !unit) && (ident.kind === "cardio" || !!distanceInLabel(node.label)));
    if (piece) {
      const last = lastFromLeaf(node, "cardio");
      out.push({ id, name, kind: "cardio", ...pieceOf(last || { metric: null }) });
      return;
    }
    const kind = ident.kind === "lift" || Number(node.weight) > 0 ? "lift" : "body";
    const sets = Array.from({ length: count }, (_, i) => {
      const kg = Math.max(0, Number(nth(node.setScheme?.weight, i, node.weight)) || 0);
      const v = Number(nth(node.setScheme?.value, i, node.value)) || 0;
      // A step for time is a timed set: a plank, or burpees for 30 s.
      if (isTimedUnit(unit)) return { kg, s: Math.max(1, Math.round(secondsOf(v, unit))) };
      return { kg, reps: Math.max(1, Math.round(v || 1)) };
    });
    out.push({ id, name, kind, sets });
  };
  walk(plan, 1, "");
  return out;
};

// A saved session's exercises and numbers, not its timings: before #164's
// plan-editor checklist this was the checklist itself.
export const checklistFromPlan = (plan) => {
  const keep = (node) => !NOT_CHECKLIST_TYPES.has(String(node?.type || "").toLowerCase());
  const prune = (node) => {
    if (!node || !keep(node)) return null;
    if (!Array.isArray(node.children)) return node;
    const children = node.children.map(prune).filter(Boolean);
    return children.length ? { ...node, children } : null;
  };
  return checklistItems(prune(plan) || {}).map((item) => ({ ...item, id: genStableId() }));
};
const NOT_CHECKLIST_TYPES = new Set(["warmup", "cooldown"]);

/** A checklist item (as checklistFromText makes) as its plan step. */
export const checklistNode = (item, list = []) => {
  if (!item) return null;
  const full = withChecklistNumbers(item, list);
  // A piece by distance alone needs a time in a plan: a guess at 5 min/km.
  const timed = full.kind === "cardio" && full.metric === "distance" && !full.s
    ? { ...full, s: Math.max(60, Math.round(((full.m || 1000) / 1000) * 300)) }
    : full;
  const view = timed.sets ? { ...timed, sets: timed.sets.map((x) => ({ ...x, at: 0 })) } : { ...timed, at: 0 };
  const { node: _drop, ...plain } = view;
  return withoutTimes(entryNode({ ...plain, id: genStableId() }));
};

/** "bench 3x8 80", "pull-ups", "row 2k" → a plan step for the checklist, or null. */
export const checklistStepFromText = (raw, list = []) => checklistNode(checklistFromText(raw, list), list);

/** The checklist with `steps` added at the end (made when there's none yet). */
export const addToChecklist = (plan, steps) => {
  const add = (Array.isArray(steps) ? steps : [steps]).filter(Boolean);
  const root =
    plan && Array.isArray(plan.children)
      ? plan
      : { id: genStableId(), type: "Group", label: CHECKLIST_LABEL, unit: "rounds", value: 1, children: [] };
  return add.length ? { ...root, children: [...root.children, ...add] } : root;
};

/** True when the checklist has nothing on it. */
export const checklistEmpty = (plan) => !plan || !Array.isArray(plan.children) || plan.children.length === 0;

/** A stored checklist as a plan: an older list of items is converted. */
export const normaliseChecklist = (stored) => {
  if (Array.isArray(stored)) return stored.length ? addToChecklist(null, stored.map((i) => checklistNode(i))) : null;
  return stored && Array.isArray(stored.children) ? stored : null;
};

// A saved session's steps as they are, fresh ids, rests and transitions out.
const copySteps = (node) => {
  if (!node || typeof node !== "object" || isRecoveryType(node.type)) return null;
  if (Array.isArray(node.children)) {
    const children = node.children.map(copySteps).filter(Boolean);
    return children.length ? { ...node, id: genStableId(), children } : null;
  }
  if (NOT_EXERCISES.has(String(node.label || "").toLowerCase())) return null;
  return { ...node, id: genStableId() };
};

/** A saved session's steps for the checklist: as they are, less its rests. */
export const checklistFromSession = (plan) =>
  (Array.isArray(plan?.children) ? plan.children : []).map(copySteps).filter(Boolean);

/**
 * Your saved sessions that have exercises to start a checklist from, newest
 * first: { id, name, at, favourite, steps, names }.
 */
export const checklistSessions = (sessions = [], limit = 10) =>
  (Array.isArray(sessions) ? sessions : [])
    .filter((s) => s && s.plan_data && typeof s.plan_data === "object" && !s.plan_data.freeTraining)
    .map((s, i) => ({ s, i, t: toTime(s) }))
    .sort((a, b) => b.t - a.t || a.i - b.i)
    .map(({ s, t }) => {
      const steps = checklistFromSession(s.plan_data);
      return {
        id: s.id ?? `t${t}`,
        name: s.workout_type || s.plan_data.label || "Session",
        at: s.started_at || s.ended_at || null,
        favourite: !!s.is_favourite,
        steps,
        names: checklistItems({ children: steps }).map((i) => i.name),
      };
    })
    .filter((x) => x.names.length > 0)
    .slice(0, limit);

// ─── The dial ───────────────────────────────────────────────────────────────

// ─── Rests ──────────────────────────────────────────────────────────────────
// Rests are marked by hand, on the session clock: { from, to, target }. `to`
// is null while you're resting; `target` is seconds to count down from, or
// null for an open rest that counts up until you're ready. They're drawn on
// the dial only, not saved.

/** Rest lengths to pick from: open, then minutes. */
export const REST_TARGETS = [null, 60, 90, 120, 180];

/** The rest you're on now, or null. */
export const openRest = (rests = []) => {
  const last = rests[rests.length - 1];
  return last && last.to == null ? last : null;
};

/** Start resting now (no change if you already are). */
export const startRest = (rests = [], now = 0, target = null) =>
  openRest(rests) ? rests : [...rests, { from: Math.round(now), to: null, target: target || null }];

/** Stop resting now. A rest that hadn't started a second ago is dropped. */
export const endRest = (rests = [], now = 0) => {
  const rest = openRest(rests);
  if (!rest) return rests;
  const to = Math.round(now);
  return to > rest.from ? [...rests.slice(0, -1), { ...rest, to }] : rests.slice(0, -1);
};

/** Change the rest you're on to count down from `target` (null: open). */
export const setRestTarget = (rests = [], target = null) => {
  const rest = openRest(rests);
  return rest ? [...rests.slice(0, -1), { ...rest, target: target || null }] : rests;
};

/**
 * Where a rest is at `now`: seconds rested, and for a timed one the seconds
 * left (negative once it's over).
 */
export const restStatus = (rest, now = 0) => {
  if (!rest) return null;
  const rested = Math.max(0, Math.round(now) - rest.from);
  return { rested, left: rest.target ? rest.target - rested : null };
};

/**
 * The stretches of the session for the Coach's dial: each runs from the
 * entry before to this one and takes its kind, with any rest cut out of it
 * as a "rest" stretch (an open rest runs to `now`). What's after the last
 * stretch is left for the caller to draw as "now".
 */
export const timelineArcs = (log = [], rests = [], now = 0) => {
  const marks = [];
  for (const e of views(log)) {
    if (e.sets) e.sets.forEach((s) => marks.push({ at: s.at, kind: e.kind }));
    else marks.push({ at: e.at, kind: e.kind, from: e.from });
  }
  marks.sort((a, b) => a.at - b.at);
  const worked = [];
  let prev = 0;
  for (const m of marks) {
    // A machine's piece knows when it started; the time before it wasn't it.
    const from = m.from != null ? Math.max(prev, m.from) : prev;
    if (m.at > from) worked.push({ from, to: m.at, kind: m.kind });
    prev = Math.max(prev, m.at);
  }
  const rested = rests
    .map((r) => ({ from: r.from, to: r.to ?? Math.max(r.from, Math.round(now)), kind: "rest" }))
    .filter((r) => r.to > r.from);
  // Each logged stretch minus the rests inside it.
  const arcs = [...rested];
  for (const w of worked) {
    let pieces = [w];
    for (const r of rested) {
      pieces = pieces.flatMap((p) => {
        if (r.to <= p.from || r.from >= p.to) return [p];
        const out = [];
        if (r.from > p.from) out.push({ ...p, to: r.from });
        if (r.to < p.to) out.push({ ...p, from: r.to });
        return out;
      });
    }
    arcs.push(...pieces);
  }
  return arcs.sort((a, b) => a.from - b.from || a.to - b.to);
};

// ─── At Finish ──────────────────────────────────────────────────────────────

/** What the log says the session was, for "What did you do?". */
export const guessActivityType = (log = []) => {
  let strength = 0;
  const cardio = {};
  for (const e of views(log)) {
    if (e.sets) strength += e.sets.length;
    else if (e.kind === "block") strength += 2;
    else {
      const id = exerciseId(e.name);
      const type =
        id === "rower" ? "row" : id === "run" ? "run" : ["spin_bike", "assault_bike"].includes(id) ? "ride" : id === "walk" ? "walk" : id === "swim" ? "swim" : null;
      if (type) cardio[type] = (cardio[type] || 0) + 3;
      else strength += 1;
    }
  }
  const best = [["strength", strength], ...Object.entries(cardio)].sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : null;
};

const same = (arr) => arr.every((v) => v === arr[0]);


const blockGroup = (entry, gap) => {
  const block = blockOf(entry.name) || BLOCKS[2];
  const rounds = Math.max(1, entry.rounds || 1);
  const work = block.work ?? Math.max(10, Math.round((gap || rounds * 60) / rounds));
  const children = [{ id: genStableId(), type: "Work", label: entry.of, unit: "s", value: work }];
  if (block.rest) children.push({ id: genStableId(), type: "Rest", label: "Rest", unit: "s", value: block.rest });
  return {
    id: genStableId(),
    type: "Group",
    label: `${block.name}: ${entry.of}`,
    unit: "rounds",
    value: rounds,
    children,
  };
};

/**
 * The log as a plan tree: a sets block per exercise (per-set reps or weight
 * as a setScheme when they changed), a timed step per piece with any
 * distance in its name (a calorie step for one done by calories), and a
 * rounds block per Tabata/EMOM/AMRAP. A piece
 * logged by distance alone takes the time since the entry before as its
 * time. Returns null for an empty log.
 */
export const logToPlan = (log = [], label = "Free training") => {
  if (!Array.isArray(log) || log.length === 0) return null;
  let prevAt = 0;
  const children = normaliseLog(log).map((node) => {
    const entry = entryView(node);
    const startAt = entry.sets ? entry.sets[0].at : entry.at;
    const gap = Math.max(0, (startAt || 0) - prevAt);
    prevAt = Math.max(prevAt, entryAt(entry));
    const step = withoutTimes(node);
    // A piece logged by distance alone takes the time since the entry before.
    if (!step.children && step.value == null) step.value = Math.round(Math.max(60, gap));
    // An AMRAP's rounds share the time it took.
    const block = entry.kind === "block" ? blockOf(entry.name) : null;
    if (block && block.work == null && step.children?.[0]) {
      const rounds = Math.max(1, entry.rounds || 1);
      step.children = [
        { ...step.children[0], value: Math.max(10, Math.round((gap || rounds * 60) / rounds)) },
        ...step.children.slice(1),
      ];
    }
    return step;
  });
  return { id: genStableId(), type: "Group", label, children };
};

// A logged node as a plan step: the same node, less when it was logged.
const withoutTimes = (node) => {
  const { at, node: _view, ...step } = node;
  return step.children ? { ...step, children: step.children.map(withoutTimes) } : step;
};
